import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaPaperPlane,
  FaCheckCircle,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import "./RSVP.css";

const RSVP = () => {
  const [formData, setFormData] = useState({
    name: "",
    attending: "",
    guest: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rsvpCode, setRsvpCode] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [guestNames, setGuestNames] = useState([]);
  const [isLoadingCode, setIsLoadingCode] = useState(false);

  // Check for RSVP code in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      setRsvpCode(code);
      fetchRSVPCode(code);
    }
  }, []);

  const fetchRSVPCode = async (code) => {
    setIsLoadingCode(true);
    try {
      const response = await fetch(`/api/rsvp-code/${code}`);
      if (response.ok) {
        const data = await response.json();
        setCodeData(data);

        // Check if all guests have already responded
        const existingRSVPs = data.existingRSVPs || [];
        const allResponded = data.names.every((name) =>
          existingRSVPs.some(
            (rsvp) => rsvp.name === name && rsvp.attending === "yes"
          )
        );

        if (allResponded && existingRSVPs.length > 0) {
          // Show existing responses instead of form
          setIsSubmitted(true);
          return;
        }

        // Filter out guests who already responded "yes"
        const availableNames = data.names.filter(
          (name) =>
            !existingRSVPs.some(
              (rsvp) => rsvp.name === name && rsvp.attending === "yes"
            )
        );

        // Initialize guest names array with available guests
        const names = availableNames.map((name) => ({
          name,
          attending: "yes",
        }));
        setGuestNames(names);

        // Auto-scroll to RSVP section when arriving with a code
        // and at least one guest still needs to respond
        if (names.length > 0) {
          setTimeout(() => {
            const el = document.getElementById("rsvp");
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        }

        // Set first name as primary name
        if (names.length > 0) {
          setFormData((prev) => ({
            ...prev,
            name: names[0].name,
          }));
        }
      } else {
        console.error("RSVP code not found");
        setRsvpCode(null);
      }
    } catch (error) {
      console.error("Error fetching RSVP code:", error);
      setRsvpCode(null);
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleGuestNameChange = (index, value) => {
    setGuestNames((prev) =>
      prev.map((guest, i) => (i === index ? { ...guest, name: value } : guest))
    );
  };

  const handleGuestAttendingChange = (index, attending) => {
    setGuestNames((prev) =>
      prev.map((guest, i) => (i === index ? { ...guest, attending } : guest))
    );
  };

  const removeGuest = (index) => {
    setGuestNames((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (rsvpCode && guestNames.length > 0) {
      // Validate guest names when using RSVP code
      const hasValidNames = guestNames.some(
        (guest) => guest.name.trim() && guest.attending === "yes"
      );
      if (!hasValidNames) {
        newErrors.guests = "Minst én person må komme";
      }
    } else {
      // Validate regular form
      if (!formData.name.trim()) {
        newErrors.name = "Navn er påkrevd";
      }

      if (!formData.attending) {
        newErrors.attending = "Vennligst velg om du kommer";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (rsvpCode && guestNames.length > 0) {
        // Submit multiple RSVPs for each guest
        const attendingGuests = guestNames.filter(
          (guest) => guest.attending === "yes" && guest.name.trim()
        );

        for (const guest of attendingGuests) {
          const response = await fetch("/api/rsvp", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code: rsvpCode,
              name: guest.name,
              attending: "yes",
              guest: "",
              message: formData.message,
            }),
          });

          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
        }
      } else {
        // Submit single RSVP
        const response = await fetch("/api/rsvp", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: rsvpCode,
            ...formData,
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting RSVP:", error);
      alert("Det oppstod en feil ved sending av RSVP. Vennligst prøv igjen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelRSVP = async (name) => {
    if (!window.confirm(`Er du sikker på at ${name} ikke kan komme?`)) {
      return;
    }

    try {
      const response = await fetch("/api/rsvp/cancel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: rsvpCode,
          name: name,
        }),
      });

      if (response.ok) {
        alert("Avmelding registrert!");
        // Refresh to show updated status
        window.location.reload();
      } else {
        throw new Error("Failed to cancel RSVP");
      }
    } catch (error) {
      console.error("Error canceling RSVP:", error);
      alert("Det oppstod en feil ved avmelding. Vennligst prøv igjen.");
    }
  };

  if (isSubmitted) {
    // Show existing RSVPs if viewing with a code
    if (rsvpCode && codeData && codeData.existingRSVPs) {
      const attending = codeData.existingRSVPs.filter(
        (r) => r.attending === "yes"
      );

      return (
        <section id="rsvp" className="rsvp section">
          <div className="container">
            <motion.div
              className="rsvp-success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <FaCheckCircle className="success-icon" />
              <h3>Påmeldte gjester</h3>
              <p>
                Dere har allerede registrert dere. Gleder oss til å feire med
                dere!
              </p>

              <div style={{ marginTop: "2rem", textAlign: "left" }}>
                {attending.map((rsvp, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "1rem",
                      marginBottom: "1rem",
                      background: "#f5f5f5",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong>{rsvp.name}</strong>
                      <p style={{ margin: "0.5rem 0", color: "#28a745" }}>
                        ✓ Kommer
                      </p>
                    </div>
                    <button
                      onClick={() => handleCancelRSVP(rsvp.name)}
                      style={{
                        padding: "0.5rem 1rem",
                        background: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Kan ikke komme
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      );
    }

    return (
      <section id="rsvp" className="rsvp section">
        <div className="container">
          <motion.div
            className="rsvp-success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaCheckCircle className="success-icon" />
            <h3>Takk for din RSVP!</h3>
            <p>Vi har mottatt din respons og gleder oss til å feire med deg!</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: "",
                  attending: "",
                  guest: "",
                  message: "",
                });
              }}
            >
              Send inn ny RSVP
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="rsvp" className="rsvp section">
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          RSVP
        </motion.h2>

        <motion.p
          className="rsvp-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Vennligst la oss vite om du kan være med på vår spesielle dag
        </motion.p>

        {isLoadingCode ? (
          <motion.div
            className="rsvp-loading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <FaSpinner className="spinner" />
            <p>Laster RSVP-informasjon...</p>
          </motion.div>
        ) : (
          <motion.form
            className="rsvp-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            {rsvpCode && codeData ? (
              // RSVP Code Form
              <>
                {guestNames.map((guest, index) => (
                  <div key={index} className="guest-form-group">
                    <div className="guest-header">
                      <h5>Gjest {index + 1}</h5>
                      {guestNames.length > 1 && (
                        <button
                          type="button"
                          className="remove-guest-btn"
                          onClick={() => removeGuest(index)}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`guest-name-${index}`}>Navn *</label>
                      <input
                        type="text"
                        id={`guest-name-${index}`}
                        value={guest.name}
                        onChange={(e) =>
                          handleGuestNameChange(index, e.target.value)
                        }
                        className={
                          !guest.name.trim() && guest.attending === "yes"
                            ? "error"
                            : ""
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>Kommer {guest.name || "denne personen"}? *</label>
                      <div className="radio-group">
                        <label
                          className={`radio-label ${
                            guest.attending === "yes" ? "selected" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name={`attending-${index}`}
                            value="yes"
                            checked={guest.attending === "yes"}
                            onChange={() =>
                              handleGuestAttendingChange(index, "yes")
                            }
                          />
                          <span className="radio-custom"></span>
                          Ja, kommer
                        </label>
                        <label
                          className={`radio-label ${
                            guest.attending === "no" ? "selected" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name={`attending-${index}`}
                            value="no"
                            checked={guest.attending === "no"}
                            onChange={() =>
                              handleGuestAttendingChange(index, "no")
                            }
                          />
                          <span className="radio-custom"></span>
                          Kan ikke delta
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                {errors.guests && (
                  <span className="error-message">{errors.guests}</span>
                )}
              </>
            ) : (
              // Regular Form
              <>
                <div className="form-group">
                  <label htmlFor="name">Fullt navn *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "error" : ""}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="guest">Følge (Valgfritt)</label>
                  <input
                    type="text"
                    id="guest"
                    name="guest"
                    value={formData.guest}
                    onChange={handleChange}
                    placeholder="Navn på følge..."
                  />
                </div>

                <div className="form-group">
                  <label>
                    {formData.guest.trim() ? "Kommer dere?" : "Kommer du?"} *
                  </label>
                  <div className="radio-group">
                    <label
                      className={`radio-label ${
                        formData.attending === "yes" ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="attending"
                        value="yes"
                        checked={formData.attending === "yes"}
                        onChange={handleChange}
                      />
                      <span className="radio-custom"></span>
                      {formData.guest.trim()
                        ? "Ja, vi kommer"
                        : "Ja, jeg kommer"}
                    </label>
                    <label
                      className={`radio-label ${
                        formData.attending === "no" ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="attending"
                        value="no"
                        checked={formData.attending === "no"}
                        onChange={handleChange}
                      />
                      <span className="radio-custom"></span>
                      {formData.guest.trim()
                        ? "Nei, vi kan ikke komme"
                        : "Nei, jeg kan ikke komme"}
                    </label>
                  </div>
                  {errors.attending && (
                    <span className="error-message">{errors.attending}</span>
                  )}
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="message">Melding (Valgfritt)</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Eventuell melding til paret..."
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner" />
                  Sender...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Send RSVP
                </>
              )}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default RSVP;
